/**
 * emailBodyToHtml — plain-text email body → clean email HTML, promoting
 * bullet / numbered lines into REAL ``<ul>`` / ``<ol>`` lists (Jul 2026).
 *
 * The agent (and legacy drafts) emit list items as literal ``• item`` /
 * ``- item`` / ``1. item`` text lines. Seeding those verbatim into the Tiptap
 * editor left the bullet CHARACTER inside a paragraph — not a real list — so
 * the user saw ``• item`` as text, and hitting the toolbar list button produced
 * a DOUBLE bullet (Tiptap's own ``•`` plus the literal one). This converter
 * turns those runs into semantic lists so:
 *   - the editor shows a real list (no literal ``•``), and
 *   - the sent ``body_html`` carries ``<ul><li>`` (renders everywhere).
 *
 * Single source of truth — used by the inbox reply "Genera con Ando" seed, the
 * send-email envelope body seed, and RichEmailBody's whole-doc AI replace.
 */

const BULLET_RE = /^\s*[•·▪‣◦*-]\s+(.*)$/;
const NUMBERED_RE = /^\s*\d+[.)]\s+(.*)$/;

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Convert a plain-text body into email HTML. Consecutive bullet / numbered
 * lines collapse into one ``<ul>`` / ``<ol>``; consecutive plain lines join
 * into a ``<p>`` with ``<br>``; blank lines separate paragraphs. Empty input →
 * ``""`` so the editor keeps its placeholder.
 */
export function emailBodyToHtml(text: string): string {
  if (!text || !text.trim()) return "";
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    // Bullet run → <ul>
    if (BULLET_RE.test(line)) {
      const items: string[] = [];
      for (; i < lines.length; i++) {
        const m = (lines[i] ?? "").match(BULLET_RE);
        if (!m) break;
        items.push(`<li>${escapeHtml((m[1] ?? "").trim())}</li>`);
      }
      out.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    // Numbered run → <ol>
    if (NUMBERED_RE.test(line)) {
      const items: string[] = [];
      for (; i < lines.length; i++) {
        const m = (lines[i] ?? "").match(NUMBERED_RE);
        if (!m) break;
        items.push(`<li>${escapeHtml((m[1] ?? "").trim())}</li>`);
      }
      out.push(`<ol>${items.join("")}</ol>`);
      continue;
    }

    // Blank line → paragraph separator
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph: gather consecutive non-list, non-blank lines (joined by <br>).
    const para: string[] = [];
    for (; i < lines.length; i++) {
      const l = lines[i] ?? "";
      if (l.trim() === "" || BULLET_RE.test(l) || NUMBERED_RE.test(l)) break;
      para.push(escapeHtml(l));
    }
    out.push(`<p>${para.join("<br>")}</p>`);
  }

  return out.join("") || "<p></p>";
}
